// [2026-09-02] ADDED — the raw API request feed: one row per call this company made.
//
// DRILL-DOWN, NOT A CHART SOURCE. The backend says the same thing at its handler: this reads a TTL
// collection whose size is proportional to traffic, so every read is bounded by an explicit page.
// The analytics screen is where trends are read; this is where a specific failed call is found.
//
// The retention window is shown in the subtitle rather than left implicit — "my call from last
// quarter is missing" is a support ticket, "older than 90 days is not kept" is an answer.

import { useState } from 'react'
import { platform } from '../../api.js'
import { credits as fmtCredits, Modal, ms, when } from '../../ui.jsx'
// [2026-09-04] CHANGED — the Badge import is dropped; every usage in this file is now PfChip
// (see the note below). badge.jsx is UNTOUCHED and still exported — the admin app imports it.
// OLD: import { Badge } from '../../components/ui/badge.jsx'
import DataTable from './DataTable.jsx'
import { PfChip } from '../../components/platform/ui.jsx'

// [2026-09-04] CHANGED — PfChip instead of the shared shadcn Badge.
// Badge reads theme.css's status palette, and proctoring-frontend-admin imports badge.jsx
// DIRECTLY (its Staff.jsx and Diagnostics.jsx), so recolouring it to the IncBot palette would
// repaint the staff dashboard. PfChip is scoped to this app and carries the product tokens, so
// the two dashboards each keep their own consistent palette.
// OLD: const CLASS_VARIANT = { success: 'healthy', client_error: 'degraded', server_error: 'critical' }
const CLASS_TONE = { success: 'live', client_error: 'warn', server_error: 'bad' }

// [2026-09-02] CHANGED — takes an optional `endpoint`. OLD signature: ({ onError }).
//
// `fetchPage` has always destructured `endpoint` and forwarded it to the API, and no caller ever
// supplied one — the filter was plumbed end to end and unreachable. It is now seeded from the
// analytics screen's endpoint table, which is the only place a customer knows the endpoint name to
// ask for. The shell remounts this screen when the value changes (see PlatformApp's `key`), so the
// seed is read fresh rather than fighting the user's own filter changes.
// [2026-09-07] CHANGED — takes an optional `service`, for the SDK / Proctoring tabs.
// OLD: export default function PlatformRequests({ onError, endpoint }) {
// Undefined by default, and qs() drops undefined keys, so the unfiltered caller sends exactly the
// query it sent before.
export default function PlatformRequests({ onError, endpoint, service }) {
  const [retention, setRetention] = useState(null)
  const [row, setRow] = useState(null)   // the drill-down modal

  // [2026-09-05] CHANGED — the panel's date range and Order By are mapped to the query parameters
  // added to GET /me/requests in the same change (date_from / date_to / order).
  // OLD: async function fetchPage({ limit, offset, search, status_class, endpoint }) {
  async function fetchPage({ limit, offset, search, status_class, endpoint,
                             dateFrom, dateTo, order }) {
    try {
      const d = await platform.requests({ limit, offset, search, status_class, endpoint, service,
                                          date_from: dateFrom, date_to: dateTo, order })
      if (d.retention_days && d.retention_days !== retention) setRetention(d.retention_days)
      return { rows: d.requests || [], total: d.total || 0 }
    } catch (err) { onError?.(err); throw err }
  }

  const columns = [
    // [2026-09-25] CHANGED — `orderSort: true`: the up/down date arrow, ordering the WHOLE result
    // set newest/oldest first through the server's `order` (see DataTable). This is the one
    // column the server sorts by (ts).
    // OLD: { key: 'ts', label: 'When', render: (r) => <span className="muted">{when(r.ts)}</span> },
    { key: 'ts', label: 'When', orderSort: true, render: (r) => <span className="muted">{when(r.ts)}</span> },
    { key: 'endpoint', label: 'Endpoint', render: (r) => <span className="mono">{r.endpoint}</span> },
    {
      key: 'status_code',
      label: 'Status',
      render: (r) => (
        <PfChip tone={CLASS_TONE[r.status_class] || 'idle'}>{r.status_code}</PfChip>
      ),
    },
    // [2026-09-15] CHANGED — the Latency and Credits COLUMNS are dropped from the SDK Requests
    // table, as asked. Only the table columns: the drill-down modal below still reports both for
    // a single call, and the API still returns `latency_ms` and `credits` on every row — nothing
    // server-side changed, so restoring a column is un-commenting the block below and nothing else.
    // THE CSV EXPORT IS UNAFFECTED and still carries both figures: downloadCsv takes its headers
    // from the raw row object (`Object.keys(rows[0])`, ui.jsx:297), not from this `columns` array,
    // so "Export page" / "Export all" keep every field the API returned. That is deliberate — the
    // screen is being simplified, not the data anyone reconciles a bill against.
    // OLD:
    // {
    //   key: 'latency_ms',
    //   label: 'Latency',
    //   align: 'right',
    //   render: (r) => (r.latency_ms == null ? '—' : ms(r.latency_ms)),
    // },
    // {
    //   key: 'credits',
    //   label: 'Credits',
    //   align: 'right',
    //   render: (r) => (r.credits ? r.credits.toFixed(3) : '—'),
    // },
  ]

  // THE DRILL-DOWN IS A REAL FILTER CONTROL, NOT A SENTENCE IN THE SUBTITLE.
  //
  // Naming it in the subtitle instead was the first version and it lies: the toolbar's Clear button
  // resets every filter, so a customer could clear the endpoint and still be told the table was
  // "filtered to /v1/analyze" while it showed everything. Declaring it as a filter means one piece
  // of state drives both the query and what the screen says about the query — the select shows
  // "Endpoint: all" the moment it is cleared, because it is reading the same value.
  //
  // One option, because this is the only endpoint the user asked about. The full list would need
  // GET /me/analytics — nine server-side reads — fetched purely to populate a dropdown on a screen
  // that does not otherwise need it. The search box already matches on endpoint for the open-ended
  // case (app/modules/me/route.py:279-280).
  const filters = [{
    key: 'status_class',
    label: 'Status',
    options: [
      { value: 'success', label: 'Success (2xx)' },
      { value: 'client_error', label: 'Client error (4xx)' },
      { value: 'server_error', label: 'Server error (5xx)' },
    ],
  }]
  if (endpoint) {
    filters.push({ key: 'endpoint', label: 'Endpoint', options: [{ value: endpoint, label: endpoint }] })
  }

  return (
    <>
      {/* [2026-09-04] CHANGED — the card title is dropped; the subtitle is KEPT.
          OLD: title="API requests"
          The page head above already reads "API Requests / Manage and track all your API Requests",
          so the title was the same words twice. The subtitle stays because it carries something the
          page head does not and that nothing else on this screen says: the RETENTION WINDOW. Without
          it, a customer looking for last month's failed call concludes the feed is broken rather
          than that the row has expired — usage_events is a TTL collection. */}
      <DataTable
        subtitle={retention
          ? `One row per API call, newest first · kept for ${retention} days`
          : 'One row per API call, newest first'}
        columns={columns}
        fetchPage={fetchPage}
        pageSize={25}
        initialFilters={endpoint ? { endpoint } : undefined}
        searchPlaceholder="Search endpoint, job id or status code…"
        filters={filters}
        filterLabel="API Requests"
        onRowClick={setRow}
        rowKey={(r, i) => `${r.ts}-${r.endpoint}-${i}`}
        emptyTitle="No API calls yet"
        emptyHint="Calls from your backend appear here as soon as you start using your API key."
        csvName="api-requests"
      />

      {row && (
        <Modal title="Request detail" subtitle={row.endpoint} onClose={() => setRow(null)}>
          <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
            {/* [2026-09-17] CHANGED — asked for by Rujal: drop Status and Latency, show what the
                call actually WAS.

                STATUS AND LATENCY ARE GONE FROM HERE, NOT FROM THE PRODUCT. Status is the badge on
                the row you clicked to open this, so it was being read twice; latency belongs on the
                Analytics screen, which charts it as a trend and a P95 where one number means
                something. A single call's 3ms tells nobody anything.

                WHAT REPLACES THEM is the request as a THING THAT HAPPENED — who it was for, and
                the id that joins it to everything else:

                  Request ID  the correlation id. It is on every log line for this call and is what
                              we hand back on a 500, so it is the one value that turns "something
                              failed around 5pm" into a single log lookup.
                  Method      GET and POST on one endpoint are different calls.
                  Candidate   the customer's OWN reference — their candidate id for an SDK call,
                              their room or interview for an analysis. `client_id` said which
                              company; nothing said which person.

                OLDER ROWS DO NOT HAVE THESE. They were added to the usage record on 2026-09-17, so
                every row written before then shows a dash — correct, and better than hiding the
                fields and making the screen look different depending on when a row was written.

                --- OLD (pre-2026-09-17) ---
                ['Status', `${row.status_code} (${row.status_class})`],
                ['Latency', row.latency_ms == null ? '—' : ms(row.latency_ms)],
                --- END OLD --- */}
            {[
              ['When', when(row.ts)],
              ['Endpoint', row.endpoint],
              ['Method', row.method || '—'],
              ['Candidate / room', row.reference || '—'],
              ['Request ID', row.request_id || '—'],
              // [2026-09-17] CHANGED — the mint now reports the credit it actually moved, so this
              // stops reading 0 on the one SDK call that costs something. Ingest is still 0 and
              // that is correct: events and screenshots are free by design.
              // [2026-09-17] CHANGED — no trailing zeros. A 0 here is CORRECT and stays: SDK
              // ingest is free by design, and the mint now reports its real credit.
              // OLD: ['Credits charged', row.credits ? row.credits.toFixed(3) : '0'],
              ['Credits charged', fmtCredits(row.credits || 0)],
              ['Job id', row.job_id || '—'],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10 }}>
                <span className="muted">{label}</span>
                <span className="mono">{value}</span>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  )
}
